import { useState, memo } from 'react';
import { Link } from 'react-router-dom';
import { DELIVERY, KIT_COPPER_METRES, STOCK, LOW_STOCK_AT } from '../data/pricing';
import { formatINR } from '../lib/quote';
import { kitLabel, nextPriceFor, leadTimeFor } from '../lib/listing';
import { addToCart, openCart } from '../lib/cart';
import { IMG_FALLBACK } from '../lib/images';
import { track, FUNNEL } from '../lib/track';

// Card-sized copies, made by scripts/resize-images.js during the build. A
// phone shows a card about 180px wide, so it downloads the 320 or 640px copy
// instead of the full photo.
const CARD_SIZES = '(min-width: 1024px) 270px, (min-width: 640px) 31vw, 46vw';
const cardSrcSet = (src) => {
  if (!src || !src.endsWith('.webp')) return undefined;
  const base = src.slice(0, -5);
  return `${base}-320.webp 320w, ${base}-640.webp 640w, ${src} 1200w`;
};

// A missing copy (the local preview before a build has made them) falls back
// to the full photo; a missing photo falls back to the placeholder.
const cardImageError = (src) => (e) => {
  const img = e.currentTarget;
  if (img.getAttribute('srcset')) {
    img.removeAttribute('srcset');
    img.src = src;
  } else {
    img.src = IMG_FALLBACK;
  }
};

export { IMG_FALLBACK } from '../lib/images';

// One card per VARIANT, built mobile-first.
//
// At two columns on a phone each card is about 160px wide, so nothing here can
// assume room: the delivery badge shortens, "Add to cart" collapses to a cart
// icon, and the image sits in a locked square so the grid doesn't jump as
// pictures load. Layout shift is a Core Web Vital, and Google measures it.

function ProductCard({ variant, index = 0, compare }) {
  const { model, kit, price, mrp, idu, inStock, soldOut } = variant;
  const [qty, setQty] = useState(1);
  const compareOn = compare ? compare.ids.includes(model.id) : false;

  const unitsLeft = (STOCK[model.id] || {})[kit] || 0;
  const nextPrice = nextPriceFor(model.id, kit);
  const save = mrp - price;
  const pct = Math.round((save / mrp) * 100);

  const add = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!inStock) {
      window.location.assign(`/request-unit?model=${model.slug}&kit=${kit}`);
      return;
    }
    addToCart(model.id, kit, qty);
    track(FUNNEL.ADD_TO_CART, { model: model.id, kit, qty, value: price * qty });
    openCart();
  };

  const step = (e, delta) => {
    e.preventDefault();
    e.stopPropagation();
    // Never more than we hold: with one left, + stays at 1.
    setQty((q) => Math.min(Math.max(1, unitsLeft), 99, Math.max(1, q + delta)));
  };

  return (
    // h-full + the button row pinned to the bottom: every card in a row is
    // the same height and the Add buttons line up (Oct 2026).
    <div className={`relative flex h-full flex-col overflow-hidden rounded-xl border bg-white transition hover:shadow-md ${
      compareOn ? 'border-blue-500 ring-1 ring-blue-500' : 'border-gray-200 hover:border-blue-300'
    }`}>
      {/* Compare sits on the photo, top right, like Flipkart and
          PolicyBazaar: always visible, and it costs the card no height. */}
      {compare && (
        <label
          className={`absolute right-1.5 top-1.5 z-10 inline-flex cursor-pointer select-none items-center gap-1 rounded-full border py-1 pl-1.5 pr-2 text-[10px] font-semibold leading-none shadow-sm transition sm:text-[11px] ${
            compareOn
              ? 'border-blue-600 bg-blue-600 text-white'
              : compare.full
                ? 'cursor-not-allowed border-gray-200 bg-white/90 text-gray-400'
                : 'border-gray-300 bg-white/95 text-gray-700 hover:border-blue-500 hover:text-blue-700'
          }`}
          title={!compareOn && compare.full ? 'You can compare up to 4 ACs' : undefined}>
          <input type="checkbox" checked={compareOn} onChange={() => compare.toggle(model.id)}
            disabled={!compareOn && compare.full}
            data-testid={`compare-tick-${model.id}`}
            className="sr-only" />
          <span aria-hidden="true" className={`flex h-3.5 w-3.5 items-center justify-center rounded-[4px] border ${
            compareOn ? 'border-white bg-white text-blue-600' : 'border-gray-400 bg-white'
          }`}>
            {compareOn && (
              <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M2.5 6.2l2.2 2.2 4.8-4.9" strokeLinecap="round" strokeLinejoin="round" /></svg>
            )}
          </span>
          {compareOn ? 'Comparing' : 'Compare'}
        </label>
      )}

      <Link to={`/${model.slug}?kit=${kit}`} className="group block min-w-0">
        <div className="relative aspect-square bg-gray-50">
          <img src={model.image} srcSet={cardSrcSet(model.image)} sizes={CARD_SIZES} alt={model.title} width="400" height="400"
            loading={index < 4 ? 'eager' : 'lazy'}
            fetchPriority={index === 0 ? 'high' : 'auto'}
            onError={cardImageError(model.image)}
            className={`h-full w-full object-contain p-2 sm:p-3 ${soldOut ? 'opacity-40' : ''}`} />
          {inStock && (
            <span className="absolute left-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-blue-600 py-1 pl-1.5 pr-2 text-[9px] font-medium leading-none text-white sm:text-[10px]">
              <svg viewBox="0 0 24 24" className="h-3 w-3 shrink-0" fill="currentColor" aria-hidden="true">
                <path d="M13 2L4.5 13.5H11l-1 8.5 8.5-11.5H12l1-8.5z" />
              </svg>
              <span className="hidden sm:inline">{DELIVERY.badge}</span>
              <span className="sm:hidden">{DELIVERY.badgeShort}</span>
            </span>
          )}
          {soldOut && (
            <span className="absolute left-1.5 top-1.5 inline-flex items-center rounded-full bg-red-600 px-2 py-1 text-[9px] font-semibold leading-none text-white sm:text-[10px]">
              Sold out
            </span>
          )}
        </div>

        <div className="min-w-0 p-2.5 pb-0 sm:p-3">
          <h3 className="line-clamp-2 min-h-[2.75em] break-words text-[12px] font-normal leading-snug text-gray-800 group-hover:text-blue-700 sm:text-[13px]">
            {model.title}
          </h3>
          <p className="mt-1 truncate text-[10px] text-gray-500 sm:text-[11px]">{idu}</p>

          <span className={`mt-1.5 inline-block rounded px-1.5 py-0.5 text-[9px] font-medium leading-none sm:text-[10px] ${
            kit === 'withKit' ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {kitLabel(kit)}
          </span>

          {inStock && (
            <p className={`mt-1 text-[10px] font-medium sm:text-[11px] ${
              unitsLeft <= LOW_STOCK_AT ? 'text-red-600' : 'text-amber-700'
            }`}>
              {nextPrice && unitsLeft === 1
                ? 'Last unit at this price'
                : unitsLeft <= LOW_STOCK_AT
                  ? `Only ${unitsLeft} left at this price`
                  : 'Limited stock at this price'}
            </p>
          )}
          {soldOut && (
            <p className="mt-1 text-[10px] font-semibold text-red-700 sm:text-[11px]">
              Sold out online · next batch in {leadTimeFor(model.id, kit)}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-baseline gap-x-1.5">
            <span className="text-[15px] font-semibold text-gray-900 sm:text-base">{formatINR(price)}</span>
            <span className="text-[11px] text-gray-500 line-through">{formatINR(mrp)}</span>
          </div>
          <p className="mt-0.5 text-[11px] font-medium text-blue-600 sm:text-xs">
            Save {formatINR(save)} · {pct}% off
          </p>
          {nextPrice && (
            <p className="mt-1">
              <span className="inline-block rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] text-stone-600 sm:text-[11px]">
                Next unit {formatINR(nextPrice)}
              </span>
            </p>
          )}
          <p className="mt-1 text-[10px] text-gray-500 sm:text-[11px]">
            ISEER {model.iseer} · {model.kw} kW
          </p>
        </div>
      </Link>

      <div className="mt-auto flex items-center gap-1.5 p-2.5 pt-2 sm:gap-2 sm:p-3">
        <div className="flex shrink-0 items-center">
          <button type="button" onClick={(e) => step(e, -1)} disabled={qty <= 1}
            aria-label="Decrease quantity"
            className="h-8 w-6 rounded-l-md border border-gray-300 text-xs text-gray-600 transition hover:bg-gray-50 disabled:opacity-30 sm:w-7">−</button>
          <span className="h-8 w-6 border-y border-gray-300 text-center text-[11px] leading-8 tabular-nums sm:w-7">{qty}</span>
          <button type="button" onClick={(e) => step(e, 1)} disabled={inStock && qty >= unitsLeft}
            aria-label="Increase quantity"
            className="h-8 w-6 rounded-r-md border border-gray-300 text-xs text-gray-600 transition hover:bg-gray-50 disabled:opacity-30 sm:w-7">+</button>
        </div>

        <button type="button" onClick={add}
          aria-label={inStock ? `Add ${model.title} to cart` : `Request ${model.title}`}
          className="flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md bg-blue-600 text-xs font-medium text-white transition hover:bg-blue-700">
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M3 3h2l2.6 12.4a1 1 0 001 .8h9.7a1 1 0 001-.8L21 7H6" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" />
          </svg>
          <span className="hidden sm:inline">{inStock ? 'Add' : 'Request'}</span>
        </button>
      </div>
    </div>
  );
}

export default memo(ProductCard);